import { useApolloClient } from '@apollo/client';
import { DialogTitle } from '@headlessui/react';
import { ACTION_CONVERT_ORDER_TO_COURTESY } from '@retainerclub/admin-api';
import { Modal } from '@retainerclub/common';
import { useToast } from '@retainerclub/hooks';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ConvertOrderConfirmation,
  ConvertOrderPreview,
  ConvertOrderSuccess,
} from './index';

const CONFIRMATION_TEXT = 'CONVERT ORDER';
const PREVIEW_TEXT = 'PREVIEW';

function ConvertOrderToCourtesyModal({
  open,
  handleCloseModal,
  order,
  refetch,
}) {
  const { setToast } = useToast();
  const client = useApolloClient();
  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState('preview'); // 'preview' | 'confirm' | 'success'
  const [previewData, setPreviewData] = useState(null);
  const [confirmationText, setConfirmationText] = useState('');
  const previewFetched = useRef(false);

  const resetState = useCallback(() => {
    setStep('preview');
    setPreviewData(null);
    setConfirmationText('');
    setIsLoading(false);
    previewFetched.current = false;
  }, []);

  useEffect(() => {
    if (!open) {
      resetState();
    }
  }, [open, resetState]);

  // Fetch preview data once when modal opens
  useEffect(() => {
    if (!(open && order?.id) || previewFetched.current) {
      return;
    }

    previewFetched.current = true;
    let cancelled = false;

    async function fetchPreview() {
      setIsLoading(true);
      try {
        const result = await client.mutate({
          mutation: ACTION_CONVERT_ORDER_TO_COURTESY,
          variables: {
            input: {
              id: order.id,
              confirmationText: PREVIEW_TEXT,
            },
          },
        });

        if (cancelled) {
          return;
        }

        const response = result.data.actionConvertOrderToCourtesy;
        if (response.success) {
          setPreviewData(response.previewData);
        } else {
          const errorMessage =
            response.errorMessages?.[0]?.message || 'Preview failed';
          setToast({
            show: true,
            title: 'Error',
            message: errorMessage,
            type: 'error',
          });
          handleCloseModal();
        }
      } catch (error) {
        if (!cancelled) {
          setToast({
            show: true,
            title: 'Error',
            message: error.message || 'An error occurred loading preview',
            type: 'error',
          });
          handleCloseModal();
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    fetchPreview();

    return () => {
      cancelled = true;
    };
  }, [open, order?.id, client, setToast, handleCloseModal]);

  async function handleSubmit() {
    if (isLoading) {
      return;
    }

    if (confirmationText !== CONFIRMATION_TEXT) {
      setToast({
        show: true,
        title: 'Error',
        message: `Confirmation text must be exactly "${CONFIRMATION_TEXT}"`,
        type: 'error',
      });
      return;
    }

    setIsLoading(true);

    try {
      const result = await client.mutate({
        mutation: ACTION_CONVERT_ORDER_TO_COURTESY,
        variables: {
          input: {
            id: order.id,
            confirmationText: CONFIRMATION_TEXT,
          },
        },
      });

      const response = result.data.actionConvertOrderToCourtesy;
      if (response.success) {
        setStep('success');
      } else {
        const errorMessage =
          response.errorMessages?.[0]?.message || 'Conversion failed';
        setToast({
          show: true,
          title: 'Error',
          message: errorMessage,
          type: 'error',
        });
      }
    } catch (error) {
      setToast({
        show: true,
        title: 'Error',
        message: error.message || 'An error occurred during conversion',
        type: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  }

  function handleClose() {
    if (step === 'success' && refetch) {
      refetch();
    }
    handleCloseModal();
  }

  function getTitle() {
    if (step === 'success') {
      return 'Conversion Complete';
    }
    if (step === 'confirm') {
      return 'Confirm Conversion';
    }
    return 'Convert to Courtesy Order';
  }

  function getSubtitle() {
    if (step === 'success') {
      return '';
    }
    if (step === 'confirm') {
      return `Confirm converting order ${order?.hexCode?.toUpperCase()} to a courtesy order`;
    }
    return `Review the changes that will be made to order ${order?.hexCode?.toUpperCase()}`;
  }

  return (
    <Modal open={open} setOpen={handleClose} size="4xl">
      <div>
        <div className="text-center">
          <DialogTitle
            as="h2"
            className="font-bold text-2xl text-gray-900 capitalize leading-6"
          >
            {getTitle()}
          </DialogTitle>
          {getSubtitle() && (
            <p className="mt-2 text-gray-600 text-sm">{getSubtitle()}</p>
          )}
        </div>
      </div>

      <div className="mt-8">
        {step === 'success' && <ConvertOrderSuccess onClose={handleClose} />}

        {step === 'preview' && previewData && (
          <>
            <ConvertOrderPreview previewData={previewData} />
            <div className="mt-6 flex justify-end space-x-3">
              <button
                type="button"
                onClick={handleClose}
                className="inline-flex items-center rounded-lg border border-gray-300 bg-white px-4 py-2 font-medium text-gray-700 text-sm shadow-sm transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => setStep('confirm')}
                className="inline-flex items-center rounded-lg border border-transparent bg-red-600 px-6 py-2 font-medium text-sm text-white shadow-sm transition-colors hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
              >
                Proceed to Confirmation
              </button>
            </div>
          </>
        )}

        {step === 'preview' && !previewData && isLoading && (
          <div className="flex items-center justify-center py-12">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-blue-600" />
            <span className="ml-3 text-gray-600 text-sm">
              Loading preview...
            </span>
          </div>
        )}

        {step === 'confirm' && (
          <ConvertOrderConfirmation
            confirmationText={confirmationText}
            onConfirmationTextChange={setConfirmationText}
            onSubmit={handleSubmit}
            onBack={() => setStep('preview')}
            isLoading={isLoading}
          />
        )}
      </div>
    </Modal>
  );
}

ConvertOrderToCourtesyModal.propTypes = {
  open: PropTypes.bool.isRequired,
  handleCloseModal: PropTypes.func.isRequired,
  order: PropTypes.object.isRequired,
  refetch: PropTypes.func,
};

export default ConvertOrderToCourtesyModal;
